import { Component, ViewEncapsulation, OnInit, OnDestroy, TemplateRef, ViewChild, ViewChildren, QueryList } from '@angular/core'
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store'
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { RequestActions } from '../../../shared/store/action/request.action';
import { SecondaryMeasurementLimits, SecondaryMeasurementUnits, Order, ReferenceBitSummary, CoGs } from '../../../shared/interfaces/rta';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { UserViewModel, Permissions } from '../../../shared/interfaces/viewmodels/userviewmodel';
import { AccountState } from '../../../shared/store/state/account.state';
import { IORD_Order, ITaskViewModel, RTAOptions, Process } from '../../../shared/interfaces/request.interface';
import { RequestState } from '../../../shared/store/state/request.state';
import { OptionState } from '../../../shared/store/state/options.state';
import { CombinedSelectors } from '../../../shared/store/state/combined.selectors';
import { OptionActions } from '../../../shared/store/action/options.action';

import * as allIcons from "@progress/kendo-svg-icons";
import { DialItem, DialItemClickEvent } from '@progress/kendo-angular-buttons';
import { AccountActions } from '../../../shared/store/action/account.action';
import { GlobalNotificationService } from '../../../shared/services/globalnotification.service';
import { BadgeAlign, BadgePosition } from '@progress/kendo-angular-indicators';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { BasicDataComponent } from '../../../shared/components/basic-data/basic-data.component';
import { SignalRService } from '../../../shared/services/signalr.service';
import { AdminService } from '../../../shared/services/admin.service';



declare var CURRENT_USER_ID: number

@Component({
    selector: 'app-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EditComponent implements OnInit, OnDestroy {

  @ViewChildren(BasicDataComponent)
  private basicDataForms!: QueryList<BasicDataComponent>;

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private notificationService: GlobalNotificationService,
    private actions$: Actions,
    private router: Router,
    private signalRService: SignalRService,
    private adminService: AdminService
  ) {
    this.activeProductLine$ = this.store.select(CombinedSelectors.activeProductLine);
    this.activeSubPSL$ = this.store.select(CombinedSelectors.activeSubPSL);
   }
  public id!: number;
  public isNewMode: boolean = false;
  public isSaving: boolean = false;
  public user$!: Observable<UserViewModel>;
  request$!: Observable<IORD_Order | undefined>;
  public rtaOptions$!: Observable<RTAOptions[]>;
  private destroy$ = new Subject<void>();
  public Size$!: number;
  public nomenclaturePending: boolean = false;
  public ProductLines_List$!: Observable<any[]>;
  public Productline: number = 0;
  public activeProductLine$!: Observable<string>;
  public activeSubPSL$!: Observable<string>;

  

  public viewChat: boolean = false;
  public viewSubmit: boolean = false;
  public viewCogs: boolean = false;
  public windowTop = 100;
  public windowLeft = 50;
  public orderLocked: boolean = false;
  public orderCompleted: boolean = false;
  public isSubmitting: boolean = false;
  public lockReason: string = '';
  public cutterMappingActive = false;
  public position: BadgePosition = "edge";
  public get align(): BadgeAlign {
    return { vertical: "bottom", horizontal: "end" };
  }
  public signalRConnected = false;

  // Dynamic action items - updated based on lock state
  public actionItems: Array<DialItem> = [];
  public fileListRefreshToken = 0;

  // Approval section properties
  public isApprovalTask: boolean = false;
  public canApprove: boolean = false;
  public approvalTaskName: string = '';
  public approvalTaskId: number = 0;
  public isApproving: boolean = false;
  public isRejecting: boolean = false;
  public viewRejectDialog: boolean = false;
  public rejectionReason: string = '';

  onDialItemClick(e: DialItemClickEvent): void {
    const args = { item: e.item, index: e.index };
    switch (args.item.label) {
      case 'Save':
        if (!this.orderLocked && !this.orderCompleted) {
          this.onSave();
        }
        return;
      case 'Submit':
        if (this.isNewMode) {
          this.notificationService.showInfoNotification('Save the basic data first to create a request ID.');
          return;
        }
        if (!this.orderLocked && !this.orderCompleted) {
          this.viewSubmit = true;
        }
        return;
      case 'CoGs':
        this.viewCogs = true;
        return;
      case 'Open Chat':
        this.viewChat = true;
        return;
      default:
        return;
    }


  }
  ngOnInit(): void {
    this.initializeSignalR();
    this.refreshActionItems();
    this.store.dispatch(new OptionActions.GetOptions({ id: CURRENT_USER_ID }));
    this.user$ = this.store.select(AccountState.user);

    // Check if we have an ID in the route
    this.route.paramMap.pipe(
      takeUntil(this.destroy$),
      map((params: ParamMap) => params.get('id'))
    ).subscribe(idParam => {
      const id = idParam ? parseInt(idParam, 10) : NaN;

      if (!isNaN(id)) {
        // Edit mode - existing request
        this.isNewMode = false;
        this.id = id;

        // Join SignalR group for this order
        this.joinOrderSignalRGroup(id);

        this.store.dispatch(new RequestActions.GetRequestDetails({ id: this.id.toString() }));
        this.store.dispatch(new OptionActions.GetAvailableCutters({ id: id, sapnumber: 0 }));
        this.store.dispatch(new OptionActions.GetCutterGroupByOrder({ id: id }));
        this.store.dispatch(new RequestActions.LoadCoGs(this.id));
        this.store.dispatch(new AccountActions.GetAllUserDetails());

        this.request$ = this.store.select(RequestState.getById).pipe(
          map(getByIdFn => getByIdFn(this.id))
        );

        // Subscribe to request changes to update lock/status state
        this.request$.pipe(
          takeUntil(this.destroy$),
          filter(request => !!request)
        ).subscribe(request => {
          this.updateOrderStatus(request!);
        });

        // Load tasks and subscribe for approval task detection
        // Wait for both tasks AND request details to be loaded before checking approval.
        // The request is needed to check the subapplication assignment.
        this.store.dispatch(new RequestActions.LoadTasks(id));
        combineLatest([
          this.store.select(RequestState.orderTasks).pipe(
            filter(tasks => !!tasks && tasks.length > 0)
          ),
          this.request$.pipe(
            filter(request => !!request && !!request.applicationId)
          )
        ]).pipe(
          takeUntil(this.destroy$)
        ).subscribe(([tasks, _request]) => {
          this.checkForApprovalTask(tasks!);
        });
      } else {
        // New mode - creating new request
        this.isNewMode = true;
        this.id = 0;

        // Initialize with empty request object for new mode
        this.request$ = of(this.createEmptyRequest());

        // Load user details for new request
        this.store.dispatch(new AccountActions.GetAllUserDetails());
      }
    });

    this.ProductLines_List$ = this.store.select(OptionState.productlines);

    // Listen for submit success to close dialog and optionally navigate
    this.actions$.pipe(
      ofActionSuccessful(RequestActions.SubmitRequestSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isSubmitting = false;
      this.viewSubmit = false;
    });

    // Listen for submit failure to reset loading state
    this.actions$.pipe(
      ofActionSuccessful(RequestActions.SubmitRequestFailure),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isSubmitting = false;
    });
  }

  private createEmptyRequest(): IORD_Order {
    const process = new Process();
    process.id = 0;
    process.name = 'Draft';
    
    return {
      id: 0,
      priority: 4,
      process: process,
      orD_CutterMap: [],
      gaugeProtections: [],
      powders: [],
      history: [],
      communication: [],
      orderFeatureAssociations: []
    } as IORD_Order;
  }

  private updateOrderStatus(request: IORD_Order): void {
    // Determine if order is locked based on process status
    // Process ID > 1 typically means it's been picked up and is in progress
    const processId = request.process?.id ?? 0;
    const processName = request.process?.name ?? '';
    
    this.orderLocked = processName != 'Order Submit';
    
    // Set lock reason based on current status
    if (this.orderLocked) {
      this.lockReason = `This order is currently "${processName}" and cannot be edited.`;
    } else {
      this.lockReason = '';
    }
    
    // Order is completed if it has a completion status (adjust based on your status IDs)
    // Common patterns: status 'Completed', 'Closed', or specific process IDs
    const completedStatuses = ['completed', 'closed', 'done'];
    const processNameLower = processName.toLowerCase();
    this.orderCompleted = completedStatuses.some(status => processNameLower.includes(status));
    
    if (this.orderCompleted) {
      this.lockReason = 'This order has been completed and cannot be modified.';
    }

    this.refreshActionItems();
  }

  private refreshActionItems(): void {
    if (this.orderLocked || this.orderCompleted) {
      this.actionItems = [
        { svgIcon: allIcons.gearsIcon, label: "CoGs" },
        { svgIcon: allIcons.commentIcon, label: "Open Chat" }
      ];
      return;
    }

    this.actionItems = [
      { svgIcon: allIcons.saveIcon, label: "Save" },
      { svgIcon: allIcons.paperPlaneIcon, label: "Submit", disabled: this.isNewMode },
      { svgIcon: allIcons.gearsIcon, label: "CoGs" },
      { svgIcon: allIcons.commentIcon, label: "Open Chat" }
    ];
  }

  /**
   * Checks the current task list (including subtasks) for an active DBTM Approval or Country Manager Approval task.
   * Also determines if the current user has permission to approve/reject.
   */
  private checkForApprovalTask(tasks: ITaskViewModel[]): void {
    const approvalProcessNames = ['DBTM Approval', 'Country Manager Approval'];

    // Search through both top-level tasks and their subtasks
    let activeApproval: { id?: number; processName?: string } | undefined;

    for (const task of tasks) {
      // Check subtasks first — approval tasks are typically subtasks within a parent task
      if (task.subTasks && task.subTasks.length > 0) {
        const match = task.subTasks.find(st =>
          approvalProcessNames.includes(st.processName ?? '') &&
          st.queue_Start !== null && st.queue_Start !== undefined &&
          (st.completeDate === null || st.completeDate === undefined) &&
          (st.kickedBack_Task_Id === null || st.kickedBack_Task_Id === undefined)
        );
        if (match) {
          activeApproval = match;
          break;
        }
      }

      // Also check top-level task itself
      if (!activeApproval &&
        approvalProcessNames.includes(task.processName ?? '') &&
        task.queue_Start !== null && task.queue_Start !== undefined &&
        (task.completeDate === null || task.completeDate === undefined) &&
        (task.kickedBack_Task_Id === null || task.kickedBack_Task_Id === undefined)
      ) {
        activeApproval = task;
        break;
      }
    }

    if (activeApproval) {
      this.isApprovalTask = true;
      this.approvalTaskName = activeApproval.processName ?? '';
      this.approvalTaskId = activeApproval.id ?? 0;

      // Determine if the current user can approve/reject
      this.canApprove = this.userCanApprove(this.approvalTaskName);
    } else {
      this.isApprovalTask = false;
      this.canApprove = false;
      this.approvalTaskName = '';
      this.approvalTaskId = 0;
    }
  }

  /**
   * Determines whether the current user has permission to approve/reject the given approval task.
   * Rules:
   *  - RTA_ADMIN can always approve any approval task.
   *  - DBTM Approval requires DBTM_APPROVE_RTA permission.
   *  - Country Manager Approval requires COUNTRY_MANAGER_APPROVE_RTA permission.
   *  - The request's subapplication must be in the user's assigned subapplications.
   */
  private userCanApprove(approvalProcessName: string): boolean {
    // RTA_ADMIN bypasses all checks
    if (this.adminService.CheckPermission(Permissions.RTA_ADMIN)) {
      return true;
    }
    // Check the task-specific permission
    let hasTaskPermission = false;
    if (approvalProcessName === 'DBTM Approval') {
      hasTaskPermission = this.adminService.CheckPermission(Permissions.DBTM_APPROVE_RTA);
    } else if (approvalProcessName === 'Country Manager Approval') {
      hasTaskPermission = this.adminService.CheckPermission(Permissions.COUNTRY_MANAGER_APPROVE_RTA);
    }
    if (!hasTaskPermission) {
      return false;
    }

    // Check that the request's subapplication is in the user's assigned subapplications
    return this.isUserAssignedToRequestSubApplication();
  }

  /**
   * Checks if the request's applicationId is within the current user's assigned subapplications.
   */
  private isUserAssignedToRequestSubApplication(): boolean {
    const user = this.store.selectSnapshot(AccountState.user);
    const assignedSubApps = user?.AssignedSubApplications ?? [];
    console.log('User assigned subapplications:', assignedSubApps);

    // If user has no assigned subapplications, they can't approve
    if (assignedSubApps.length === 0) {
      return false;
    }

    // Get the request's applicationId from the current request snapshot
    const getByIdFn = this.store.selectSnapshot(RequestState.getById);
    const request = getByIdFn ? getByIdFn(this.id) : undefined;
    console.log('Current request:', request);
    const requestAppId = request?.applicationId;

    if (!requestAppId) {
      return false;
    }

    // Check if the request's applicationId matches any of the user's assigned subapplication IDs
    return assignedSubApps.some(sa => sa.subApplication_Id === requestAppId);
  }

  onApprove(): void {
    if (!this.canApprove || this.isApproving || this.approvalTaskId === 0) return;
    this.isApproving = true;
    this.store.dispatch(new RequestActions.ApproveOrderTask(this.approvalTaskId, this.id));
    // Reset after dispatch (state handles success/failure)
    setTimeout(() => { this.isApproving = false; }, 2000);
  }

  onReject(): void {
    if (!this.canApprove) return;
    this.rejectionReason = '';
    this.viewRejectDialog = true;
  }

  onCancelReject(): void {
    this.viewRejectDialog = false;
    this.rejectionReason = '';
  }

  onConfirmReject(): void {
    if (this.isRejecting || this.approvalTaskId === 0) return;
    if (!this.rejectionReason.trim()) {
      this.notificationService.showErrorNotification('Please provide a reason for rejection.');
      return;
    }
    this.isRejecting = true;
    this.store.dispatch(new RequestActions.RejectOrderTask(this.approvalTaskId, this.rejectionReason, this.id));
    this.viewRejectDialog = false;
    this.rejectionReason = '';
    // Reset after dispatch
    setTimeout(() => { this.isRejecting = false; }, 2000);
  }

  onSizeChanged(size: number) {
    this.Size$ = size;
  }
  onNomenclaturePendingChanged(pending: boolean) {
    this.nomenclaturePending = pending;
  }

  public onTabSelect(event: SelectEvent): void {
    this.cutterMappingActive = event.index === 2;
  }
  onProductlineChanged(productline: number) {
    this.Productline = productline;
  }
  async onSave(): Promise<boolean> {
    // Always validate required fields before saving
    let allValid = true;
    this.basicDataForms.forEach(child => {
      if (!child.validateForm()) {
        allValid = false;
      }
    });
    if (!allValid) {
      this.notificationService.showErrorNotification('Please fill in all required fields.');
      return false;
    }

    this.isSaving = true;

    try {

      if (this.isNewMode) {
        // New mode: Create new request
        await firstValueFrom(
          this.store.dispatch(new RequestActions.SaveDraft({ draftId: undefined, userId: CURRENT_USER_ID }))
        );

        // Get the newly created request from state
        const createdRequest = await firstValueFrom(this.store.select(RequestState.getRequest));

        if (createdRequest?.id) {
          // Update URL without full navigation (replaceUrl keeps browser history clean)
          this.isNewMode = false;
          this.id = createdRequest.id;
          await this.router.navigate(['/edit', createdRequest.id], { replaceUrl: true });
          this.notificationService.showSuccessNotification('Request created successfully!');

          // Load additional data for the new request
          this.store.dispatch(new OptionActions.GetAvailableCutters({ id: this.id, sapnumber: 0 }));
          this.store.dispatch(new OptionActions.GetCutterGroupByOrder({ id: this.id }));
          this.store.dispatch(new RequestActions.LoadCoGs(this.id));

          // Update request$ to point to the new request
          this.request$ = this.store.select(RequestState.getById).pipe(
            map(getByIdFn => getByIdFn(this.id))
          );
        }
      } else {
        // Edit mode: Update existing request
        this.store.dispatch(new RequestActions.SaveDraft({ draftId: this.id, userId: CURRENT_USER_ID }));
        this.store.dispatch(new RequestActions.LoadCoGs(this.id));
        this.notificationService.showSuccessNotification('Changes saved.');
      }
      return true;
    } catch (error: any) {
      // Error notification is already shown by the state action; only show a generic
      // fallback if no specific message was surfaced.
      if (!error?.message) {
        this.notificationService.showErrorNotification('Failed to save. Please try again.');
      }
      return false;
    } finally {
      this.isSaving = false;
    }
  }

  onSubmit(): void {
    if (this.isSubmitting) return; // Prevent double submission

    if (this.nomenclaturePending) {
      this.notificationService.showErrorNotification('Cannot submit: a nomenclature override request is pending approval. Please wait for approval or retract the request.');
      return;
    }

    this.isSubmitting = true;

    // Validate, save, then submit — abort at each step if it fails
    this.onSave().then(saved => {
      if (!saved) {
        this.isSubmitting = false;
        return;
      }
      this.user$.pipe(take(1)).subscribe(() => {
        this.store.dispatch(new RequestActions.SubmitRequest({ orderId: this.id, userId: CURRENT_USER_ID }));
        // isSubmitting reset by the action success/failure listeners in ngOnInit
      });
    }).catch(() => {
      this.isSubmitting = false;
    });
  }

  onCancelSubmit(): void {
    if (this.isSubmitting) return; // Don't allow cancel during submission
    this.viewSubmit = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.leaveOrderSignalRGroup();
    this.signalRService.stopConnection();
  }

  private initializeSignalR(): void {
    if (!this.signalRConnected) {
      this.signalRService.startConnection().then(() => {
        this.signalRConnected = true;
        console.log('SignalR connection established from EditComponent');
      }).catch(err => {
        console.error('SignalR connection error:', err);
      });
    }
  }

  private joinOrderSignalRGroup(orderId: number): void {
    if (this.signalRConnected) {
      this.signalRService.joinOrderGroup(orderId);
    } else {
      // Wait for connection then join
      const checkConnection = setInterval(() => {
        if (this.signalRConnected) {
          this.signalRService.joinOrderGroup(orderId);
          clearInterval(checkConnection);
        }
      }, 100);
      // Clear interval after 10 seconds to prevent memory leak
      setTimeout(() => clearInterval(checkConnection), 10000);
    }
  }

  private leaveOrderSignalRGroup(): void {
    if (this.signalRConnected && this.id) {
      this.signalRService.leaveOrderGroup(this.id);
    }
  }

  getBitFeatureIds(order: IORD_Order | undefined): number[] {
    return (order?.orderFeatureAssociations ?? [])
      .map(feature => feature.bitFeatureID)
      .filter((id): id is number => typeof id === 'number');
  }
  public getProductLine(): Observable<string> {
    return this.request$.pipe(
      take(1),
      map(x =>
        this.ProductLines_List$.pipe(
          take(1),
          map(p => {
            if ((p as any)?.value === x?.productLine) {
              console.log((p as any)?.name)
              return (p as any)?.name || 'NA';
            }
            return 'NA';
          })
        )
      ),
      map(innerObservable => innerObservable as unknown as string)
    );
  }
  
  public getSubPSL(): Observable<string> {
    return this.request$.pipe(
      take(1),
      map(x =>
        this.ProductLines_List$.pipe(
          take(1),
          map(p => {
            if ((p as any)?.value === x?.productLine) {
              return (p as any)?.subcategory || 'NA';
            }
            return 'NA';
          })
        )
      ),
      map(innerObservable => innerObservable as unknown as string)
    );
  }
  
  public onUploadCompleted(item: any): void {
    this.fileListRefreshToken++;
  }

}

